import { useState } from 'react';
import { useApi } from '@/hooks/useApi';
import {
  MapPin,
  Navigation,
  Clock,
  AlertTriangle,
  Zap,
  Timer,
  Shield,
  Search,
  Radio,
} from 'lucide-react';

interface TrackedVehicle {
  id: string;
  name: string;
  lat: number;
  lng: number;
  speed: number;
  status: 'moving' | 'idle' | 'offline';
  heading: number;
  lastUpdate: string;
}

interface GPSData {
  tracked: number;
  moving: number;
  idle: number;
  alerts: number;
  speedViolations: number;
  extendedIdle: number;
  geofenceAlerts: number;
  vehicles: TrackedVehicle[];
  recentAlerts: { id: string; vehicle: string; type: string; message: string; time: string }[];
  geofences: { id: string; name: string; type: string; vehicleCount: number }[];
}

const EMPTY: GPSData = {
  tracked: 0,
  moving: 0,
  idle: 0,
  alerts: 0,
  speedViolations: 0,
  extendedIdle: 0,
  geofenceAlerts: 0,
  vehicles: [],
  recentAlerts: [],
  geofences: [],
};

const STATUS_DOT: Record<string, string> = {
  moving: 'bg-emerald-400',
  idle: 'bg-amber-400',
  offline: 'bg-zinc-500',
};

export default function GPSTrackingPage() {
  const { data, error } = useApi<GPSData>('/api/gps');
  const d = data ?? EMPTY;
  const [search, setSearch] = useState('');

  const filtered = d.vehicles.filter((v) =>
    v.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/10">
          <MapPin className="w-6 h-6 text-emerald-500" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-100">GPS Tracking</h1>
          <p className="text-sm text-zinc-400">Real-time fleet location monitoring</p>
        </div>
      </div>

      {error && (
        <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-sm text-red-400">Unable to load GPS data. Please try again.</span>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Radio} label="Tracked Vehicles" value={d.tracked} />
        <StatCard icon={Navigation} label="Moving" value={d.moving} accent />
        <StatCard icon={Clock} label="Idle" value={d.idle} />
        <StatCard icon={AlertTriangle} label="Alerts" value={d.alerts} warn />
      </div>

      {/* Alert Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <AlertCard icon={Zap} label="Speed Violations" value={d.speedViolations} color="red" />
        <AlertCard icon={Timer} label="Extended Idle" value={d.extendedIdle} color="amber" />
        <AlertCard icon={Shield} label="Geofence Alerts" value={d.geofenceAlerts} color="orange" />
      </div>

      {/* Map + Sidebar */}
      <div className="flex gap-4">
        {/* Sidebar */}
        <div className="w-72 flex-shrink-0 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search vehicles..."
              className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none focus:border-emerald-500/50"
            />
          </div>

          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {filtered.map((v) => (
              <div
                key={v.id}
                className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-3 hover:border-emerald-500/30 transition-colors cursor-pointer"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-zinc-100 truncate">{v.name}</span>
                  <span className={`flex items-center gap-1 text-xs ${v.status === 'moving' ? 'text-emerald-400' : v.status === 'idle' ? 'text-amber-400' : 'text-zinc-500'}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[v.status]}`} />
                    {v.status}
                  </span>
                </div>
                <div className="text-xs text-zinc-500 mb-1">
                  {v.lat.toFixed(3)}, {v.lng.toFixed(3)}
                </div>
                <div className="flex items-center justify-between">
                  {v.speed > 0 ? (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                      {v.speed} mph
                    </span>
                  ) : (
                    <span className="text-xs text-zinc-600">Stationary</span>
                  )}
                  <span className="text-xs text-zinc-600">{v.lastUpdate}</span>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="text-center text-xs text-zinc-500 py-6">No vehicles match</p>
            )}
          </div>
        </div>

        {/* Map */}
        <div className="flex-1">
          <div className="bg-zinc-900/50 backdrop-blur border-2 border-dashed border-emerald-500/30 rounded-xl h-[520px] flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-10 h-10 text-emerald-500/40 mx-auto mb-3" />
              <p className="text-sm text-zinc-400 font-medium">Connect GPS provider for real-time tracking</p>
              <p className="text-xs text-zinc-600 mt-1">Supports Samsara, Geotab, GPS Trackit, and more</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Alerts */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide mb-3">Recent Alerts</h3>
          {d.recentAlerts.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">No recent alerts</p>
          ) : (
            <div className="space-y-3">
              {d.recentAlerts.map((a) => (
                <div key={a.id} className="flex items-start gap-3">
                  <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-200">{a.vehicle}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400">{a.type}</span>
                    </div>
                    <p className="text-xs text-zinc-500">{a.message}</p>
                  </div>
                  <span className="text-xs text-zinc-600 flex-shrink-0">{a.time}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Geofences */}
        <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wide mb-3">Geofences</h3>
          {d.geofences.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4 text-center">No geofences configured</p>
          ) : (
            <div className="space-y-3">
              {d.geofences.map((g) => (
                <div key={g.id} className="flex items-center justify-between p-3 bg-zinc-800/40 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm font-medium text-zinc-200">{g.name}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-500">{g.type}</span>
                  </div>
                  <span className="text-xs text-zinc-400">{g.vehicleCount} vehicle{g.vehicleCount !== 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  accent,
  warn,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  accent?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="bg-zinc-900/50 backdrop-blur border border-zinc-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-zinc-500" />
        <span className="text-xs font-medium text-zinc-400 uppercase tracking-wide">{label}</span>
      </div>
      <span className={`text-2xl font-bold ${accent ? 'text-emerald-400' : warn ? 'text-red-400' : 'text-zinc-100'}`}>
        {value}
      </span>
    </div>
  );
}

function AlertCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  color: 'red' | 'amber' | 'orange';
}) {
  const styles = {
    red: { text: 'text-red-400', bg: 'bg-red-500/10', border: 'border-red-500/20' },
    amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
    orange: { text: 'text-orange-400', bg: 'bg-orange-500/10', border: 'border-orange-500/20' },
  };
  const s = styles[color];
  return (
    <div className={`bg-zinc-900/50 backdrop-blur border rounded-xl p-5 ${s.border}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`p-1.5 rounded-lg ${s.bg}`}>
            <Icon className={`w-4 h-4 ${s.text}`} />
          </div>
          <span className="text-sm text-zinc-300">{label}</span>
        </div>
        <span className={`text-2xl font-bold ${s.text}`}>{value}</span>
      </div>
    </div>
  );
}
